import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone, ArrowUpRight } from "lucide-react";
import Logo from "@/components/Logo";
import { NAV_LINKS, CONTACT } from "@/data/site";
import { EASE } from "@/components/Reveal";

export default function Navbar() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setOpen(false);
    }, [location.pathname]);

    useEffect(() => {
        document.body.style.overflow = open ? "hidden" : "";
        return () => {
            document.body.style.overflow = "";
        };
    }, [open]);

    return (
        <>
            <header
                data-testid="main-header"
                className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
                    scrolled
                        ? "border-b border-line/70 bg-night/85 backdrop-blur-md"
                        : "border-b border-transparent bg-transparent"
                }`}
            >
                <div className="mx-auto flex h-[72px] w-full max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-12">
                    <Logo />
                    <nav className="hidden items-center gap-8 lg:flex">
                        {NAV_LINKS.map((l) => (
                            <NavLink
                                key={l.path}
                                to={l.path}
                                data-testid={l.testid}
                                className="group relative py-2 font-mono text-[0.7rem] uppercase tracking-[0.22em] text-slate-300 transition-colors hover:text-white"
                            >
                                {({ isActive }) => (
                                    <>
                                        <span className={isActive ? "text-accent" : ""}>{l.label}</span>
                                        <span
                                            className={`absolute inset-x-0 bottom-0 h-px origin-left bg-accent transition-transform duration-300 ${
                                                isActive
                                                    ? "scale-x-100"
                                                    : "scale-x-0 group-hover:scale-x-100"
                                            }`}
                                        />
                                    </>
                                )}
                            </NavLink>
                        ))}
                    </nav>
                    <div className="flex items-center gap-3">
                        <Link
                            to="/contact"
                            data-testid="nav-enquire-btn"
                            className="group hidden items-center gap-2 bg-accent px-6 py-3 font-display text-sm font-bold uppercase tracking-widest text-night transition-colors duration-300 hover:bg-accent-hover sm:flex"
                        >
                            Enquire Now
                            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                        </Link>
                        <button
                            data-testid="mobile-menu-btn"
                            onClick={() => setOpen(!open)}
                            aria-label="Toggle menu"
                            className="flex h-11 w-11 items-center justify-center border border-line text-white transition-colors hover:border-accent hover:text-accent lg:hidden"
                        >
                            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                        </button>
                    </div>
                </div>
            </header>

            <AnimatePresence>
                {open && (
                    <motion.div
                        data-testid="mobile-menu"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="fixed inset-0 z-40 flex flex-col bg-night/97 backdrop-blur-lg lg:hidden"
                    >
                        <div className="flex flex-1 flex-col justify-center gap-2 px-8 pt-20">
                            {NAV_LINKS.map((l, i) => (
                                <motion.div
                                    key={l.path}
                                    initial={{ opacity: 0, x: -32 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: 0.08 * i + 0.1, duration: 0.5, ease: EASE }}
                                >
                                    <NavLink
                                        to={l.path}
                                        data-testid={`mobile-${l.testid}`}
                                        onClick={() => setOpen(false)}
                                        className={({ isActive }) =>
                                            `block border-b border-line/60 py-4 font-display text-4xl font-bold uppercase tracking-wide ${
                                                isActive ? "text-accent" : "text-white"
                                            }`
                                        }
                                    >
                                        {l.label}
                                    </NavLink>
                                </motion.div>
                            ))}
                        </div>
                        <motion.div
                            initial={{ opacity: 0, y: 24 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.45, duration: 0.5, ease: EASE }}
                            className="space-y-4 px-8 pb-10"
                        >
                            <div className="flex flex-col gap-2">
                                {CONTACT.phones.map((p) => (
                                    <a
                                        key={p.href}
                                        href={p.href}
                                        data-testid={`mobile-call-${p.href.slice(6)}`}
                                        className="flex items-center gap-3 font-mono text-sm text-slate-300"
                                    >
                                        <Phone className="h-4 w-4 text-accent" />
                                        {p.display}
                                    </a>
                                ))}
                            </div>
                            <Link
                                to="/contact"
                                data-testid="mobile-enquire-btn"
                                onClick={() => setOpen(false)}
                                className="flex items-center justify-center gap-2 bg-accent px-6 py-4 font-display text-base font-bold uppercase tracking-widest text-night"
                            >
                                Enquire Now
                                <ArrowUpRight className="h-4 w-4" />
                            </Link>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
}
